import React, {
  Children,
  cloneElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { ScrollButton } from '@/components/shared/streamline/StreamlineControls';
import { isEmpty } from '@/shared/utility';

interface InfiniteScrollStreamlineProps {
  spacing?: 0 | 16 | 32 | 48;
  children: ReactElement[];
  autoplay?: boolean;
  stretch?: boolean;
}

/* SPECS:
This component is complex and has lots of moving parts. These comments will help you navigate through the storm.
What this component does is that is takes Array of children, clones it two times and puts it in front and in back of the original array.
When user scrolls/clicks through the banners, if they go outside of the original array (into the clones array),
on scroll-end it will instantly scroll back to the same place in the original array, showing original child instead of clone.
That instant switch is what gives the effect of infinite loop.
Stretch - this prop makes it so that Streamline has no paddings, so it takes full height/width of the container
Autoplay - automatically switches to the next banner every 5s
Spacing - param which will be used in the future

TODO:
- Add support for itemsPerPage > 1 - in theory it should work out of the box, without any spacing between children
- Add support for spacing between children
- Add support for children of different widths
- Improve re-rendering on having children with react navigation
 */

const InfiniteScrollStreamline = ({
  children,
  spacing = 0,
  autoplay = false,
}: InfiniteScrollStreamlineProps) => {
  // REFS
  const scrollableContainerRef = React.useRef<HTMLDivElement>(null);

  // BEHAVIOUR STATES
  const [itemsPerPage, setItemsPerPage] = useState(1);
  // This stops user from clicking on the arrows while scroll is not finished
  const scrollClickLockedRef = useRef(false);
  // Widths of each children, indexes match the children array
  // Right now childrenWidths[0] is used across the code, but it's because all children currently have the same width
  const [childrenWidths, setChildrenWidths] = useState<number[]>([]);
  // IntervalID used to stop the interval on user interaction (stops autoplay when user interacts with component)
  const intervalIdRef = useRef<NodeJS.Timer | null>(null);

  // MEMOIZED CLONES
  const childrenClones = useMemo(
    () =>
      Children.map(children, child =>
        cloneElement(child, { key: child.key + 'infinite-scroll-child' }),
      ),
    [children, itemsPerPage],
  );

  const childrenPreClones = useMemo(
    () =>
      Children.map(children, child =>
        cloneElement(child, { key: child.key + 'infinite-scroll-prechild' }),
      ),
    [children, itemsPerPage],
  );

  // Children + clones
  const presentableChildren = useMemo(
    () =>
      children.length === 1 ? children : childrenPreClones.concat(children).concat(childrenClones),
    [children, childrenClones, childrenPreClones],
  );

  // CALLBACKS
  const getChildrenWidths = useCallback(() => {
    const container = scrollableContainerRef.current;

    if (container) {
      const childNodes = Array.from(container.children) as HTMLElement[];
      const widths = childNodes.map(child => child.offsetWidth);
      setChildrenWidths(widths);
    }
  }, []);

  // This function is called when user clicks on the arrows and for autoplay loop
  const handleScrollClick = useCallback(
    (direction: 'left' | 'right', stopAutoplay = false) => {
      // So no more clicking of the arrows while scrollend is not done
      const container = scrollableContainerRef.current;
      if (container && !isEmpty(childrenWidths)) {
        scrollClickLockedRef.current = true;
        const left = direction === 'left';
        const scrollDistance = childrenWidths[0];

        // If there is current autoplay loop going on, stop it and only run user interaction scroll
        if (autoplay && stopAutoplay && intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          // Start new interval
          intervalIdRef.current = setInterval(() => handleScrollClick('right'), 5000);
        }

        scrollableContainerRef.current?.scrollBy({
          behavior: 'smooth',
          left: left ? -scrollDistance : scrollDistance,
        });
      }
    },
    [autoplay, childrenWidths],
  );

  /*
  This is the most important function. It handles all scrolling and calculates if user is in cloned array.
  If yser is past maximum points it will revert them to the same spot in the original array
   */
  const handleScroll = useCallback((): void => {
    // handle scroll will run before any user interaction, so we can enable user clicks again
    scrollClickLockedRef.current = false;
    const container = scrollableContainerRef.current;
    if (!container || childrenWidths.length === 0) return;

    const scrollPos = container.scrollLeft;

    // If scrolled past original items to the right (end)
    if (scrollPos >= 2 * children.length * childrenWidths[0]) {
      container.scrollLeft = scrollPos - children.length * childrenWidths[0];
    }
    // If scrolled past original items to the left (start)
    else if (scrollPos < children.length * childrenWidths[0]) {
      container.scrollLeft = scrollPos + children.length * childrenWidths[0];
    }
  }, [childrenWidths, itemsPerPage]);

  // This method is to stop autoplay loop for mobile users. When interacting with component this will stop intervals.
  const handleMobileTouch = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      // Start new interval
      intervalIdRef.current = setInterval(() => handleScrollClick('right'), 5000);
    }
  }, [autoplay, handleScrollClick]);

  // EFFECTS
  // This effect starts the autoplay each time the interval is ended
  useEffect(() => {
    if (autoplay && !isEmpty(childrenWidths)) {
      intervalIdRef.current = setInterval(() => handleScrollClick('right'), 5000);
    }
    return () => {
      intervalIdRef.current && clearInterval(intervalIdRef.current);
    };
  }, [autoplay, handleScrollClick, childrenWidths]);

  // This effect calculates maximum number of fully visible items per page
  useEffect(() => {
    if (scrollableContainerRef.current && childrenWidths[0]) {
      setItemsPerPage(
        Math.round(scrollableContainerRef.current.clientWidth / (childrenWidths[0] - 30)),
      );
    }
  }, [childrenWidths[0]]);

  // This effect looks for window resizing and calculates children widths
  useEffect(() => {
    window.addEventListener('resize', getChildrenWidths);
    return () => {
      window.removeEventListener('resize', getChildrenWidths);
    };
  }, [getChildrenWidths]);

  // This effect listens to scrollend events and on them runs handleScroll
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (container) {
      container.addEventListener('scrollend', handleScroll);
      const timeoutId = setTimeout(handleScroll, 100);
      return () => {
        container.removeEventListener('scrollend', handleScroll);
        clearTimeout(timeoutId);
      };
    }
  }, [handleScroll]);

  // This effect updates childrenWidths field
  useEffect(() => {
    if (scrollableContainerRef.current) {
      getChildrenWidths();
    }
  }, [childrenClones, getChildrenWidths]);

  // This effect is used to always first land on the first child in original array
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (container) {
      container.scrollLeft = children.length * childrenWidths[0];
    }
  }, [childrenWidths, children]);

  // UI
  return (
    <div className="relative">
      <div className="absolute inset-0 top-[50%]">
        <ScrollButton
          direction="left"
          onClick={() => !scrollClickLockedRef.current && handleScrollClick('left', true)}
          show={children.length > 1}
        />
        <ScrollButton
          direction="right"
          onClick={() => !scrollClickLockedRef.current && handleScrollClick('right', true)}
          show={children.length > 1}
        />
      </div>
      <div
        className={classNames(
          'flex overflow-x-scroll no-scrollbar overflow-y-hidden snap-x snap-mandatory',
          {
            // SNAP MANDATORY - needed for this to work on mobile phones
            'space-x-[0px]': spacing === 0,
            'space-x-[16px]': spacing === 16,
            'space-x-[32px]': spacing === 32,
            'space-x-[48px]': spacing === 48,
          },
        )}
        ref={scrollableContainerRef}
        onTouchStart={() => handleMobileTouch()}
      >
        {presentableChildren}
      </div>
    </div>
  );
};

export default InfiniteScrollStreamline;
